import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ACCOUNTS = ["Cash", "Bank", "UPI"];

function optionsFor(f, masters, values) {
  if (f.type === "distributor") return (masters.distributors || []).filter((d) => d.status === "Active").map((d) => [d.code, d.name]);
  if (f.type === "fund") {
    const types = f.types ? f.types(values || {}) : null;
    const list = (masters.accounts || []).filter((a) => a.status === "Active" && (!types || types.includes(a.type)));
    return (f.required ? [] : [["__none", "Default / none"]]).concat(list.map((a) => [a.account_id, `${a.account_name}${a.upi_id ? ` · ${a.upi_id}` : ""} (${a.type})`]));
  }
  if (f.type === "dse") return masters.dse.filter((m) => m.status === "Active").map((m) => [m.dse_id, `${m.dse_id} · ${m.dse_name}`]);
  if (f.type === "fl") return masters.fl.filter((m) => m.status === "Active").map((m) => [m.fl_id, `${m.fl_id} · ${m.fl_name}`]);
  if (f.type === "party") return (masters.party || []).filter((m) => m.status === "Active").map((m) => [m.party_id, `${m.party_id} · ${m.party_name} (${m.distributor_id})`]);
  if (f.type === "retailer") return (masters.retailer || []).filter((m) => m.status === "Active").map((m) => [m.retailer_id, `${m.retailer_id} · ${m.retailer_name}`]);
  if (f.type === "scheme") return masters.schemes.map((s) => [s.name, `${s.name} (₹${s.rate}/unit)`]);
  if (f.type === "account") return (f.required ? [] : [["__none", "None"]]).concat(ACCOUNTS.map((a) => [a, a]));
  return (f.options || []).map((o) => (Array.isArray(o) ? o : [o, o]));
}

function DistAlloc({ id, value, onChange, masters }) {
  const rows = Array.isArray(value) && value.length ? value : [{ distributor_id: "", amount: "" }];
  const set = (i, k, x) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: x } : r)));
  const dists = (masters.distributors || []).filter((d) => d.status === "Active");
  return (
    <div className="space-y-2" data-testid={id}>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <Select value={r.distributor_id} onValueChange={(x) => set(i, "distributor_id", x)}>
            <SelectTrigger className="flex-1" data-testid={`${id}-dist-${i}`}><SelectValue placeholder="Distributor Account" /></SelectTrigger>
            <SelectContent>{dists.map((d) => <SelectItem key={d.code} value={d.code} data-testid={`${id}-dist-${i}-option-${d.code}`}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="number" step="0.01" min="0" placeholder="Amount ₹" className="num w-32" value={r.amount} onChange={(e) => set(i, "amount", e.target.value)} data-testid={`${id}-amount-${i}`} />
          {rows.length > 1 && <button type="button" className="text-xs text-rose-600" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`${id}-remove-${i}`}>Remove</button>}
        </div>
      ))}
      <button type="button" className="text-xs text-blue-700 hover:underline" onClick={() => onChange([...rows, { distributor_id: "", amount: "" }])} data-testid={`${id}-add`}>+ Add distributor split</button>
    </div>
  );
}

export function FieldInput({ f, value, onChange, masters, testPrefix, values }) {
  const id = `${testPrefix}-${f.name.replace(/_/g, "-")}`;
  const label = (
    <Label htmlFor={id} className="text-xs font-medium text-slate-600">
      {f.label} {f.required && <span className="text-red-600">*</span>}
    </Label>
  );
  let input;
  if (f.type === "dist_alloc") {
    input = <DistAlloc id={id} value={value} onChange={onChange} masters={masters} />;
  } else if (["select", "dse", "fl", "party", "retailer", "account", "scheme", "fund", "distributor"].includes(f.type)) {
    const opts = optionsFor(f, masters, values);
    input = (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={id}><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent>
          {opts.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No options — add in Masters</div>}
          {opts.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${id}-option-${v}`}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  } else if (f.type === "textarea") {
    input = <Textarea id={id} data-testid={id} rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  } else {
    const type = f.type === "date" ? "date" : f.type === "time" ? "time" : f.type === "money" || f.type === "number" ? "number" : "text";
    input = (
      <Input id={id} data-testid={id} type={type} step={f.type === "money" ? "0.01" : "any"} min={type === "number" ? "0" : undefined}
        className={type === "number" ? "num" : ""} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    );
  }
  return (
    <div className="space-y-1.5">
      {label}
      {input}
      {f.hint && <p className="text-[11px] text-slate-500">{f.hint}</p>}
    </div>
  );
}
